/**
 * L4-KILL-002: KillNotificationManager Service
 *
 * Processes unit_died events, resolves creature portraits,
 * manages notification lifecycle, and notifies subscribers of updates.
 */

import type { CombatEvent, CombatState } from '@drawn-of-war/shared/types/combat';

/**
 * Kill notification data structure
 */
export interface KillNotification {
  /** Unique notification ID (timestamp-based) */
  id: string;
  /** Unit that got the kill */
  victorUnitId: string;
  /** Creature ID for reference */
  victorCreatureId: string;
  /** Pre-resolved portrait URL */
  victorPortraitUrl: string;
  /** Creature name for accessibility */
  victorCreatureName: string;
  /** Unit that died */
  defeatedUnitId: string;
  /** Creature ID for reference */
  defeatedCreatureId: string;
  /** Pre-resolved portrait URL */
  defeatedPortraitUrl: string;
  /** Creature name for accessibility */
  defeatedCreatureName: string;
  /** When notification was created */
  timestamp: number;
  /** Current fade state */
  fadeState: 'entering' | 'visible' | 'fading' | 'hidden';
}

/**
 * Configuration for KillNotificationManager
 */
export interface KillNotificationManagerConfig {
  /** Maximum notifications to display (default: 5) */
  maxNotifications: number;
  /** Time in visible state (default: 3000ms) */
  displayDuration: number;
  /** Fade out duration (default: 500ms) */
  fadeDuration: number;
  /** Entrance animation duration (default: 200ms) */
  enterDuration: number;
}

/**
 * Subscriber callback type
 */
type NotificationSubscriber = (notifications: KillNotification[]) => void;

/**
 * Creature document interface for portrait lookup
 * Minimal interface - any object with name and sprites.menuSprite works
 */
export interface CreatureLookupDocument {
  name: string;
  sprites: { menuSprite: string };
}

/**
 * KillNotificationManager
 *
 * Manages kill notification lifecycle with state machine:
 * entering (200ms) -> visible (3000ms) -> fading (500ms) -> hidden (removed)
 *
 * Features:
 * - FIFO queue for notifications
 * - Maximum stack limit (default 5)
 * - Subscribe/unsubscribe pattern for React components
 * - Automatic cleanup on dispose
 */
export class KillNotificationManager {
  private notifications: KillNotification[] = [];
  private subscribers: Set<NotificationSubscriber> = new Set();
  private config: Required<KillNotificationManagerConfig>;
  private timers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  constructor(config: Partial<KillNotificationManagerConfig> = {}) {
    this.config = {
      maxNotifications: config.maxNotifications ?? 5,
      displayDuration: config.displayDuration ?? 3000,
      fadeDuration: config.fadeDuration ?? 500,
      enterDuration: config.enterDuration ?? 200,
    };
  }

  /**
   * Process a unit_died event and create notification if killer is known
   *
   * @param event - Combat event (must be unit_died type)
   * @param combatState - Current combat state with units array
   * @param creatureLookup - Map of creatureId -> creature document for portraits
   */
  public processDeathEvent(
    event: CombatEvent,
    combatState: CombatState,
    creatureLookup: Map<string, CreatureLookupDocument>
  ): void {
    // Validate event type
    if (event.eventType !== 'unit_died') {
      console.warn('[KillNotificationManager] Received non-death event');
      return;
    }

    const { unitId, killedByUnitId } = event.data;

    // Skip if no killer attribution (environmental death, timeout, etc.)
    if (!killedByUnitId) {
      console.log('[KillNotificationManager] Death without killer attribution, skipping notification');
      return;
    }

    // Find units in combat state
    const defeatedUnit = combatState.units.find(u => u.unitId === unitId);
    const victorUnit = combatState.units.find(u => u.unitId === killedByUnitId);

    if (!defeatedUnit || !victorUnit) {
      console.warn('[KillNotificationManager] Could not find units in combat state', {
        unitId,
        killedByUnitId,
        availableUnits: combatState.units.map(u => u.unitId)
      });
      return;
    }

    // Lookup creature documents for portraits
    const defeatedCreature = creatureLookup.get(defeatedUnit.creatureId);
    const victorCreature = creatureLookup.get(victorUnit.creatureId);

    if (!defeatedCreature || !victorCreature) {
      console.warn('[KillNotificationManager] Could not find creature documents', {
        defeatedCreatureId: defeatedUnit.creatureId,
        victorCreatureId: victorUnit.creatureId
      });
      return;
    }

    // Create notification
    const notification: KillNotification = {
      id: this.generateId(),
      victorUnitId: victorUnit.unitId,
      victorCreatureId: victorUnit.creatureId,
      victorPortraitUrl: victorCreature.sprites.menuSprite,
      victorCreatureName: victorCreature.name,
      defeatedUnitId: defeatedUnit.unitId,
      defeatedCreatureId: defeatedUnit.creatureId,
      defeatedPortraitUrl: defeatedCreature.sprites.menuSprite,
      defeatedCreatureName: defeatedCreature.name,
      timestamp: Date.now(),
      fadeState: 'entering',
    };

    this.addNotification(notification);
  }

  /**
   * Get current active notifications
   * Note: All notifications in the array are active (we remove them instead of marking hidden)
   */
  public getActiveNotifications(): KillNotification[] {
    return [...this.notifications];
  }

  /**
   * Subscribe to notification updates
   * @returns Unsubscribe function
   */
  public subscribe(callback: NotificationSubscriber): () => void {
    this.subscribers.add(callback);
    // Immediately call with current state (wrapped in try-catch like other notifications)
    try {
      callback(this.getActiveNotifications());
    } catch (error) {
      console.error('[KillNotificationManager] Subscriber error:', error);
    }

    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Clear all notifications (for combat end)
   */
  public clear(): void {
    // Clear all timers
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();

    // Clear notifications
    this.notifications = [];
    this.notifySubscribers();
  }

  /**
   * Dispose of manager resources
   */
  public dispose(): void {
    this.clear();
    this.subscribers.clear();
  }

  // --- Private Methods ---

  private addNotification(notification: KillNotification): void {
    // Add to front of array (newest first)
    this.notifications.unshift(notification);

    // Enforce max notifications
    while (this.notifications.length > this.config.maxNotifications) {
      const removed = this.notifications.pop();
      if (removed) {
        this.clearTimers(removed.id);
      }
    }

    this.notifySubscribers();

    // Schedule state transitions
    this.scheduleEnterComplete(notification.id);
  }

  private scheduleEnterComplete(id: string): void {
    const timer = setTimeout(() => {
      this.updateFadeState(id, 'visible');
      this.scheduleFadeStart(id);
    }, this.config.enterDuration);

    this.timers.set(`${id}-enter`, timer);
  }

  private scheduleFadeStart(id: string): void {
    const timer = setTimeout(() => {
      this.updateFadeState(id, 'fading');
      this.scheduleRemoval(id);
    }, this.config.displayDuration);

    this.timers.set(`${id}-visible`, timer);
  }

  private scheduleRemoval(id: string): void {
    const timer = setTimeout(() => {
      // Remove notification directly (no 'hidden' state - we just remove it)
      this.notifications = this.notifications.filter(n => n.id !== id);
      this.clearTimers(id);
      this.notifySubscribers();
    }, this.config.fadeDuration);

    this.timers.set(`${id}-fade`, timer);
  }

  private updateFadeState(id: string, state: KillNotification['fadeState']): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      notification.fadeState = state;
      this.notifySubscribers();
    }
  }

  private clearTimers(id: string): void {
    for (const suffix of ['enter', 'visible', 'fade']) {
      const timerId = `${id}-${suffix}`;
      const timer = this.timers.get(timerId);
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(timerId);
      }
    }
  }

  private notifySubscribers(): void {
    const activeNotifications = this.getActiveNotifications();
    for (const callback of this.subscribers) {
      try {
        callback(activeNotifications);
      } catch (error) {
        console.error('[KillNotificationManager] Subscriber error:', error);
      }
    }
  }

  private generateId(): string {
    return `kill-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
